/**
 * How a configurable product's option values are presented.
 *
 * Both rules here answer complaints from the "Hemsidan 2.0" feedback about the
 * same product — the Recknagel rail, which carries 33 rifle models on one
 * `th_specifikation` attribute (p.12).
 */

/**
 * Above this many values an option is drawn as a dropdown instead of a row of
 * buttons.
 *
 * Buttons win for a handful: every choice is visible and one tap away. They
 * stop winning once the row wraps, and at 33 they become a wall that buries the
 * add-to-cart button — which is what p.12 is asking about ("Ger det några
 * fördelar att ha en artikel utan rullista?"). The old site used a dropdown
 * here, and for a list that long it was right.
 *
 * 8 is Torsbo's own call, tightened from the 12 first shipped: two rows of
 * buttons is about where a list stops being scannable at a glance in the buy
 * box's width, and a calibre or size axis rarely exceeds that.
 */
export const OPTIONS_AS_SELECT_FROM = 8;

/**
 * Order option values for display.
 *
 * Magento returns `configurable_options.values` in NO guaranteed order, and in
 * practice the order differs between requests — three separate reads of the
 * Recknagel rail gave three different orders. That is visible to customers as
 * options reshuffling under them: adding the product to favourites triggers a
 * refresh inside `useWishlist`, the page re-renders from a fresh query, and the
 * 33 models come back rearranged.
 *
 * Sorting by label fixes the reshuffle and reads better besides, because these
 * labels are "make - model" so alphabetical order groups every Remington
 * together. `sv` collation keeps å/ä/ö where a Swedish reader expects them, and
 * `numeric` keeps "Strl 9" before "Strl 10" on the size attributes.
 *
 * NOTE: this is display order only. Variant matching is by option id, so it is
 * unaffected — see `getSelectedVariant` in product-details.tsx.
 *
 * The proper fix is a sort order in the backend; Magento holds a `position` per
 * option (the old site's `Likipe\Eav` override sorted by exactly that) but does
 * not expose it through the `configurable_options` GraphQL field, so the
 * storefront cannot honour it.
 */
export function sortOptionValues<T extends { label: string }>(values: readonly T[]): T[] {
  const collator = new Intl.Collator('sv', { numeric: true, sensitivity: 'base' });
  return [...values].sort((a, b) => collator.compare(a.label, b.label));
}

/**
 * How large to draw an option's visual swatch.
 *
 * `md` is the default: a colour chip is legible at a glance, and a fabric photo
 * reads fine small.
 *
 * `lg` is double that, for attributes whose swatch is a line drawing rather
 * than a colour — a reticle's whole point is the pattern of its lines, and p.10
 * of the "Hemsidan 2.0" feedback asks for the picture precisely because the
 * names alone are "lite förvirrande för kunderna". At the colour chip's size a
 * crosshair is an unreadable smudge.
 */
export type SwatchSize = 'md' | 'lg';

/** Attributes whose swatch needs the larger box. */
const LARGE_SWATCH_ATTRIBUTES: readonly string[] = ['th_riktmedel'];

export function swatchSize(attributeCode: string): SwatchSize {
  return LARGE_SWATCH_ATTRIBUTES.includes(attributeCode) ? 'lg' : 'md';
}
